import permissions from "app/permissions";
import { getPermissions } from "auth/security/securityUtils";
import { studentRequests } from "api/studentApi";
import { Box, Typography, Paper, useMediaQuery } from "@mui/material";
import {
  CreateButton,
  Datagrid,
  EditButton,
  FunctionField,
  List,
  ListActions,
  ReferenceField,
  ShowButton,
  SimpleList,
  TextField,
  useStore,
} from "react-admin";
import SecuredComponent from "server/security/SecuredComponent";
import { checkPermission } from "server/security/utils";
import { WeekDaysChoices } from "utils/choices";
import CustomFilter from "features/CustomFilter";
import useSemesterFilter from "features/filters/useSemesterFilter";
import { administrationRequests } from "api/administrationApi";
import { MOBILE_BREAKPOINT } from "utils/constants";
import { settings } from "app";
import { MobileListRow } from "features/MobileListRow";

const STORE_KEY = "foreignLanguagesSchedule.filters";

const ListForeignLanguagesSchedules = () => {
  const [filters, setFilters] = useStore(STORE_KEY, null);
  const isMobile = useMediaQuery(MOBILE_BREAKPOINT);
  const semester = useSemesterFilter({ filters, withLanguage: true, withStudyType: true });

  const handleSearch = selectedFilters => {
    setFilters(selectedFilters);
  };

  const renderCourse = record =>
    record.course === 1
      ? "1-ви Курс"
      : record.course === 2
        ? "2-ри Курс"
        : record.course === 3
          ? "3-ти Курс"
          : record.course === 4
            ? "4-ти Курс"
            : record.course === 5
              ? "5-ти Курс"
              : record.course === 6
                ? "6-ти Курс"
                : "-";

  return (
    <>
      <CustomFilter
        title="Избери разписание"
        cardWidth={isMobile ? "100%" : "700px"}
        filters={semester.fields}
        buttonLabel="Покажи"
        buttonDisabled={!semester.isComplete}
        buttonOnClick={() => handleSearch(semester.query)}
      />
      {!filters ? (
        <Paper sx={{ mt: 5 }}>
          <Box
            p={2}
            sx={{ display: "flex", justifyContent: "center", gap: 3 }}
          >
            <Typography
              align="center"
              color="secondary"
            >
              Въведи филтри за разписание чужди езици или създай ново.
            </Typography>
            <CreateButton />
          </Box>
        </Paper>
      ) : (
        <List
          title="Разписания чужди езици"
          filter={filters}
          exporter={false}
          actions={
            <ListActions
              hasCreate={checkPermission(getPermissions(), permissions.canManageForeignLanguageActivities)}
            />
          }
        >
          {isMobile ? (
            <SimpleList
              linkType={false}
              sx={settings.style.mobileList}
              primaryText={record => (
                <div>
                  <MobileListRow fields={[{ label: "Чужд език", value: record?.foreignLanguageNameBg }]} />
                  <MobileListRow fields={[{ label: "Ниво", value: record?.foreignLanguageLevelNameBg }]} />
                </div>
              )}
              secondaryText={record => (
                <div>
                  <MobileListRow
                    fields={[
                      {
                        label: "Структурна единица",
                        value: (
                          <ReferenceField
                            source="structuralUnitId"
                            reference={administrationRequests.getStructuralUnitsSelect()}
                          >
                            <TextField source="name" />
                          </ReferenceField>
                        ),
                      },
                    ]}
                  />
                  <MobileListRow
                    fields={[
                      {
                        label: "Семестър",
                        value: (
                          <ReferenceField
                            source="academicSessionId"
                            reference={studentRequests.getAcademicSessionsSelect()}
                          >
                            <TextField source="sessionTypeText" />
                          </ReferenceField>
                        ),
                      },
                    ]}
                  />
                  <MobileListRow fields={[{ label: "Курс", value: renderCourse(record) }]} />
                  <MobileListRow
                    fields={[
                      { label: "Ден", value: WeekDaysChoices.find(d => d.id === record.weekDay)?.name },
                    ]}
                  />
                  <MobileListRow
                    fields={[
                      {
                        label: "Час",
                        value: `${record.startTime.slice(0, 5)} - ${record.endTime.slice(0, 5)}`,
                      },
                    ]}
                  />
                </div>
              )}
              tertiaryText={() => (
                <>
                  <SecuredComponent permission={permissions.canManageForeignLanguageActivities}>
                    <EditButton label="" />
                  </SecuredComponent>
                  <SecuredComponent permission={permissions.canPreviewForeignLanguageActivities}>
                    <ShowButton label="" />
                  </SecuredComponent>
                </>
              )}
            />
          ) : (
            <Datagrid
              rowClick={false}
              bulkActionButtons={false}
            >
              <TextField
                source="foreignLanguageNameBg"
                label="Чужд език"
              />
              <TextField
                source="foreignLanguageLevelNameBg"
                label="Ниво"
              />
              <ReferenceField
                source="structuralUnitId"
                reference={administrationRequests.getStructuralUnitsSelect()}
                label="Структурна единица"
              >
                <TextField source="name" />
              </ReferenceField>
              <ReferenceField
                source="academicSessionId"
                label="Семестър"
                reference={studentRequests.getAcademicSessionsSelect()}
              >
                <TextField source="sessionTypeText" />
              </ReferenceField>
              <FunctionField
                label="Курс"
                render={renderCourse}
              />
              <FunctionField
                label="Ден"
                render={record => WeekDaysChoices.find(d => d.id === record.weekDay)?.name}
              />
              <FunctionField
                label="Час"
                render={record => `${record.startTime.slice(0, 5)} - ${record.endTime.slice(0, 5)}`}
              />
              <SecuredComponent permission={permissions.canManageForeignLanguageActivities}>
                <EditButton />
              </SecuredComponent>
              <SecuredComponent permission={permissions.canPreviewForeignLanguageActivities}>
                <ShowButton />
              </SecuredComponent>
            </Datagrid>
          )}
        </List>
      )}
    </>
  );
};

export default ListForeignLanguagesSchedules;
